import { ErrorMessage } from "formik";
import React, { Fragment, useContext } from "react";
import { Col, FormControl, FormGroup, Grid, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ProtocolContext from "../../containers/Protocols/ProtocolContext/ProtocolContext";
import { conceptType, snomedExpressions } from "../../variables/Enums";
import Card from "../Cards/Card";
import { CustomSelect } from "../CustomControls/CustomSelect";
import { PractitionerSelect } from "../CustomControls/PractitionerSelect";
import { SnomedPicker } from "../CustomControls/SnomedPicker";
import { Save } from "../Icons/Icons";
import { dateFormatter } from "../Utils/Formatter";
import { FormErrorMessage } from "../Validations/FormValidationErrors";

export const Brief = () => {
  const { t } = useTranslation();
  const context = useContext(ProtocolContext);

  if (!context || context.isLoading) {
    return null;
  }

  return (
    <Fragment>
      <Card
        tabs
        content={
          <Fragment>
            <Grid fluid>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Row>
                      <Col sm={3}>
                        <div className="card-label">
                          {t("protocols.brief.fullTitle")}
                        </div>
                      </Col>
                      <Col sm={9}>
                        <div className="card-text">
                          {context.protocol.fullTitle}
                        </div>
                      </Col>
                    </Row>
                  </FormGroup>
                  <FormGroup>
                    <Row>
                      <Col sm={3}>
                        <div className="card-label">
                          {t("protocols.brief.specialty")}
                        </div>
                      </Col>
                      <Col sm={9}>
                        <div className="card-text">
                          {context.protocol.specialty?.description}
                        </div>
                      </Col>
                    </Row>
                  </FormGroup>
                  <FormGroup>
                    <Row>
                      <Col sm={3}>
                        <div className="card-label">
                          {t("protocols.brief.indication")}
                        </div>
                      </Col>
                      <Col sm={9}>
                        <div className="card-text">
                          {context.protocol.indication?.description}
                        </div>
                      </Col>
                    </Row>
                  </FormGroup>
                  <FormGroup>
                    <Row>
                      <Col sm={3}>
                        <div className="card-label">
                          {t("protocols.brief.patientsObjective")}
                        </div>
                      </Col>
                      <Col sm={9}>
                        <div className="card-text">
                          {context.protocol.patientsObjective}
                        </div>
                      </Col>
                    </Row>
                  </FormGroup>
                  <FormGroup>
                    <Row>
                      <Col sm={3}>
                        <div className="card-label">
                          {t("protocols.brief.keywords")}
                        </div>
                      </Col>
                      <Col sm={9}>
                        {context.protocol.keywords &&
                          context.protocol.keywords.map((x, idx) => {
                            return (
                              <div
                                key={`keywords-${idx}`}
                                className="card-text"
                              >
                                {x.description}
                              </div>
                            );
                          })}
                      </Col>
                    </Row>
                  </FormGroup>
                  <FormGroup>
                    <Row>
                      <Col sm={3}>
                        <div className="card-label">
                          {t("protocols.brief.protocolNumber")}
                        </div>
                      </Col>
                      <Col sm={4}>
                        <div className="card-text">
                          {context.protocol.protocolNumber}
                        </div>
                      </Col>
                    </Row>
                  </FormGroup>
                  <FormGroup>
                    <Row>
                      <Col sm={3}>
                        <div className="card-label">
                          {t("protocols.brief.centerNumber")}
                        </div>
                      </Col>
                      <Col sm={4}>
                        <div className="card-text">
                          {context.protocol.centerNumber}
                        </div>
                      </Col>
                    </Row>
                  </FormGroup>
                </Col>
              </Row>
            </Grid>
          </Fragment>
        }
      />
    </Fragment>
  );
};

export const BriefForm = (props) => {
  const {
    values,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    errors,
    touched,
  } = props;

  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.authentication.user);
  const responsabilitiesOptions = [
    {
      name: t("clinicalTrials.general.roleSubInvestigator"),
      value: 1,
    },
    {
      name: t("clinicalTrials.general.roleStudyCoordinator"),
      value: 2,
    },
    {
      name: t("clinicalTrials.general.roleDataManager"),
      value: 3,
    },
    {
      name: t("clinicalTrials.general.rolePharmacist"),
      value: 4,
    },
    {
      name: t("clinicalTrials.general.roleNursing"),
      value: 5,
    },
    {
      name: t("clinicalTrials.general.roleAdministrative"),
      value: 6,
    },
  ];

  if (!values.brief) {
    values.brief = {
      laboratory: "",
      title: "",
      fullTitle: "",
      phase: "",
      attachments: [],
      patientsObjective: 0,
      status: {},
      indication: "",
      subIndications: null,
      principalInvestigator: "",
      protocolNumber: "",
      centerNumber: "",
      studyRole: "",
      formNTC: "",
      eudraTC: "",
      specialty: "",
      version: 0,
      currentUserStudyRole: "",
    };
  }

  if (!values.brief.id && !values.brief.studyTasks)
    values.brief.studyTasks = props.masterData.StudyTasks;

  if (
    values.brief.id &&
    !values.brief.studyTasks &&
    props.masterData.StudyTasks &&
    props.masterData.StudyTasks.length > 0
  ) {
    values.brief.studyTasks = props.masterData.StudyTasks.filter(
      (x) =>
        -1 !==
        values.brief.protocolStudyTasks.findIndex((y) => y.studyTaskId === x.id)
    );
  }

  const title = values.brief.created
    ? t("protocols.brief.editProtocol") + " " + values.brief.title
    : t("clinicalTrials.brief.addClinicalTrial");
  const subtitle = values.brief.created && (
    <div>
      {t("protocols.brief.created")}{" "}
      {dateFormatter(values.brief.created, "DD/MMM/YYYY HH:mm")}
    </div>
  );

  const isCurrentUserPI =
    values?.brief?.principalInvestigator?.userId === currentUser.sub;

  return (
    <Card
      title={title}
      subtitle={subtitle}
      ctFullWidth
      content={
        <form onSubmit={handleSubmit} autoComplete="off">
          <Grid fluid>
            <Row>
              <Col md={6}>
                <FormGroup
                  style={{
                    marginBottom: "4px",
                  }}
                >
                  <Row>
                    <Col sm={3}>
                      <div className="card-label">
                        {t("clinicalTrials.startup.sponsor")}
                      </div>
                    </Col>
                    <Col sm={9}>
                      <CustomSelect
                        value={values.brief.laboratory}
                        name={`brief.laboratory`}
                        placeholder={t("commons.select")}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        options={props.masterData.Laboratories}
                      />
                      <ErrorMessage
                        name={`brief.laboratory`}
                        component={FormErrorMessage}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup
                  style={{ marginBottom: "4px" }}
                  validationState={touched?.brief?.title && errors?.brief?.title ? "error" : null}
                >
                  <Row>
                    <Col sm={3}>
                      <div className="card-label">
                        {t("protocols.brief.title")} *
                      </div>
                    </Col>
                    <Col sm={9}>
                      <FormControl
                        type="text"
                        name={`brief.title`}
                        value={values.brief.title}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoComplete="off"
                      />
                      <ErrorMessage
                        name={`brief.title`}
                        component={FormErrorMessage}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup
                  style={{
                    marginBottom: "4px",
                  }}
                  controlId="formNTC"
                >
                  <Row>
                    <Col sm={3}>
                      <div className="card-label">
                        {t("clinicalTrials.startup.ntc")}
                      </div>
                    </Col>
                    <Col sm={9}>
                      <FormControl
                        type="text"
                        name={`brief.formNTC`}
                        value={values.brief.formNTC}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoComplete="off"
                      />
                      <ErrorMessage
                        name={`brief.title`}
                        component={FormErrorMessage}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup
                  style={{
                    marginBottom: "4px",
                  }}
                  controlId="formEudraTC"
                >
                  <Row>
                    <Col sm={3}>
                      <div className="card-label">
                        {t("clinicalTrials.startup.eudraTc")}
                      </div>
                    </Col>
                    <Col sm={9}>
                      <FormControl
                        type="text"
                        name={`brief.eudraTC`}
                        value={values.brief.eudraTC}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoComplete="off"
                      />
                      <ErrorMessage
                        name={`brief.title`}
                        component={FormErrorMessage}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup
                  style={{
                    marginBottom: "4px",
                  }}
                >
                  <Row>
                    <Col sm={3}>
                      <div className="card-label">
                        {t("protocols.brief.fullTitle")}
                      </div>
                    </Col>
                    <Col sm={9}>
                      <FormControl
                        componentClass="textarea"
                        name={`brief.fullTitle`}
                        value={values.brief.fullTitle}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoComplete="off"
                      />
                      <ErrorMessage
                        name={`brief.fullTitle`}
                        component={FormErrorMessage}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup
                  style={{ marginBottom: "4px" }}
                  validationState={touched?.brief?.specialty && errors?.brief?.specialty ? "error" : null}
                >
                  <Row>
                    <Col sm={3}>
                      <div className="card-label">
                        {t("clinicalTrials.startup.specialty")} *
                      </div>
                    </Col>
                    <Col sm={9}>
                      <SnomedPicker
                        conceptType={conceptType.events}
                        defaultSelected={
                          values.brief.specialty ? [values.brief.specialty] : []
                        }
                        id={`specialty`}
                        value={values.brief.specialty}
                        name={`brief.specialty`}
                        placeholder={t("commons.search")}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        expression={snomedExpressions.EspecialidadesMedicas}
                      />
                      <ErrorMessage
                        name={`brief.specialty`}
                        component={FormErrorMessage}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup
                  style={{ marginBottom: "4px" }}
                  validationState={touched?.brief?.indication && errors?.brief?.indication ? "error" : null}
                >
                  <Row>
                    <Col sm={3}>
                      <div className="card-label">
                        {t("protocols.brief.indication")} *
                      </div>
                    </Col>
                    <Col sm={9}>
                      <SnomedPicker
                        conceptType={conceptType.events}
                        defaultSelected={
                          values.brief.indication
                            ? [values.brief.indication]
                            : []
                        }
                        id={`brief.indication`}
                        value={values.brief.indication}
                        name={`brief.indication`}
                        placeholder={t("commons.search")}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        conceptId={"64572001"} // Trastorno
                      />
                      <ErrorMessage
                        name={`brief.indication`}
                        component={FormErrorMessage}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup
                  style={{
                    marginBottom: "4px",
                  }}
                >
                  <Row>
                    <Col sm={3}>
                      <div className="card-label">
                        {t("clinicalTrials.startup.keywords")}
                      </div>
                    </Col>
                    <Col sm={9}>
                      <SnomedPicker
                        conceptType={conceptType.drugs}
                        defaultSelected={
                          values.brief.subIndications
                            ? [values.brief.subIndications]
                            : []
                        }
                        id={`brief.subIndications`}
                        value={values.brief.subIndications}
                        name={`brief.subIndications`}
                        placeholder={t("commons.search")}
                        multiple={true}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        expression={`< 64572001 OR < ${snomedExpressions.Sustancia} OR < ${snomedExpressions.Medicamentos}`} // Trastorno o Sustancia o Producto
                      />
                      <ErrorMessage
                        name={`brief.keywords`}
                        component={FormErrorMessage}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup
                  style={{ marginBottom: "4px" }}
                  validationState={touched?.brief?.principalInvestigator && errors?.brief?.principalInvestigator ? "error" : null}
                >
                  <Row>
                    <Col sm={3}>
                      <div className="card-label">
                        {t("protocols.brief.principalInvestigator")} *
                      </div>
                    </Col>
                    <Col sm={9}>
                      <PractitionerSelect
                        value={values.brief.principalInvestigator}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        name={`brief.principalInvestigator`}
                        placeholder={t("commons.select")}
                      />
                      <ErrorMessage
                        name={`brief.principalInvestigator`}
                        component={FormErrorMessage}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup
                  style={{ marginBottom: "4px" }}
                  validationState={touched?.brief?.studyRoles && errors?.brief?.studyRoles ? "error" : null}
                >
                  <Row>
                    <Col sm={3}>
                      <div className="card-label">
                        {t("protocols.brief.studyRole")} *
                      </div>
                    </Col>
                    <Col sm={9}>
                      <CustomSelect
                        value={values.brief.studyRoles}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        name={`brief.studyRoles`}
                        options={props.studyRoles}
                        getOptionLabel={(x) => x.name}
                        placeholder={t("commons.select")}
                      />
                      <ErrorMessage
                        name={`brief.studyRoles`}
                        component={FormErrorMessage}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                {
                  // Muestro usuario actual solo si no fue seleccionado como investigador principal
                  values.brief.principalInvestigator?.userId !==
                    currentUser.sub && (
                    <>
                      <FormGroup
                        style={{
                          marginBottom: "4px",
                        }}
                      >
                        <Row>
                          <Col sm={3}>
                            <div className="card-label">
                              {t("protocols.brief.currentUser")}
                            </div>
                          </Col>
                          <Col sm={9}></Col>
                        </Row>
                      </FormGroup>
                      <FormGroup
                        style={{ marginBottom: "4px" }}
                        validationState={touched?.brief?.currentUserResponsability && errors?.brief?.currentUserResponsability ? "error" : null}
                      >
                        <Row>
                          <Col sm={3}>
                            <div className="card-label">
                              {t(
                                "clinicalTrials.brief.currentUserResponsability"
                              )} *
                            </div>
                          </Col>
                          <Col sm={9}>
                            <CustomSelect
                              value={values.brief.currentUserResponsability}
                              onChange={setFieldValue}
                              onBlur={setFieldTouched}
                              name={`brief.currentUserResponsability`}
                              options={responsabilitiesOptions}
                              getOptionLabel={(x) => x.name}
                              getOptionValue={(x) => x.value}
                              placeholder={t("commons.select")}
                            />
                            <ErrorMessage
                              name={`brief.currentUserResponsability`}
                              component={FormErrorMessage}
                            />
                          </Col>
                        </Row>
                      </FormGroup>
                      <FormGroup
                        validationState={touched?.brief?.currentUserStudyRole && errors?.brief?.currentUserStudyRole ? "error" : null}
                      >
                        <Row>
                          <Col sm={3}>
                            <div className="card-label">
                              {t("protocols.brief.studyRole")} *
                            </div>
                          </Col>
                          <Col sm={9}>
                            <CustomSelect
                              value={values.brief.currentUserStudyRole}
                              onChange={setFieldValue}
                              onBlur={setFieldTouched}
                              name={`brief.currentUserStudyRole`}
                              options={props.studyRoles.filter(
                                (x) =>
                                  !values.brief.studyRoles ||
                                  x.id !== values.brief.studyRoles.id
                              )}
                              getOptionLabel={(x) => x.name}
                              placeholder={t("commons.select")}
                            />
                            <ErrorMessage
                              name={`brief.currentUserStudyRole`}
                              component={FormErrorMessage}
                            />
                          </Col>
                        </Row>
                      </FormGroup>
                    </>
                  )
                }
              </Col>
              <Col md={6}>
                <FormGroup
                  style={{ marginBottom: "4px" }}
                  validationState={touched?.brief?.phase && errors?.brief?.phase ? "error" : null}
                >
                  <Row>
                    <Col sm={3}>
                      <div className="card-label">
                        {t("protocols.brief.phase")} *
                      </div>
                    </Col>
                    <Col sm={9}>
                      <CustomSelect
                        value={values.brief.phase}
                        name={`brief.phase`}
                        placeholder={t("commons.select")}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        options={props.masterData.Phases}
                      />
                      <ErrorMessage
                        name={`brief.phase`}
                        component={FormErrorMessage}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup
                  style={{ marginBottom: "4px" }}
                  validationState={touched?.brief?.protocolNumber && errors?.brief?.protocolNumber ? "error" : null}
                >
                  <Row>
                    <Col sm={3}>
                      <div className="card-label">
                        {t("protocols.brief.protocolNumber")} *
                      </div>
                    </Col>
                    <Col sm={9}>
                      <FormControl
                        type="text"
                        name={`brief.protocolNumber`}
                        value={values.brief.protocolNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoComplete="off"
                      />
                      <ErrorMessage
                        name={`brief.protocolNumber`}
                        component={FormErrorMessage}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup
                  style={{ marginBottom: "4px" }}
                  validationState={touched?.brief?.centerNumber && errors?.brief?.centerNumber ? "error" : null}
                >
                  <Row>
                    <Col sm={3}>
                      <div className="card-label">
                        {t("protocols.brief.centerNumber")} *
                      </div>
                    </Col>
                    <Col sm={9}>
                      <FormControl
                        type="text"
                        name={`brief.centerNumber`}
                        value={values.brief.centerNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoComplete="off"
                      />
                      <ErrorMessage
                        name={`brief.centerNumber`}
                        component={FormErrorMessage}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col sm={3}>
                      <div className="card-label">
                        {t("clinicalTrials.startup.version")}
                      </div>
                    </Col>
                    <Col sm={9}>
                      <FormControl
                        type="text"
                        name={`brief.version`}
                        value={values.brief.version}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoComplete="off"
                      />
                      <ErrorMessage
                        name={`brief.version`}
                        component={FormErrorMessage}
                      />
                    </Col>
                  </Row>
                </FormGroup>
              </Col>
            </Row>
          </Grid>
          <Grid fluid>
            <Row>
              <Col sm={12}>
                <button
                  type="submit"
                  className="btn btn-success btn-right"
                  disabled={
                    props.isSubmitting ||
                    !props.isValid ||
                    !values.brief.laboratory ||
                    !values.brief.version ||
                    (!isCurrentUserPI &&
                      values.brief.studyRoles?.id ===
                        values.brief.currentUserStudyRole?.id)
                  }
                >
                  <Save />
                  {t("buttons.save")}
                </button>
                <button
                  type="button"
                  className="btn btn-default  btn-right"
                  onClick={props.onCancel}
                  style={{ marginRight: "10px" }}
                >
                  {t("buttons.cancel")}
                </button>
              </Col>
            </Row>
          </Grid>
        </form>
      }
    />
  );
};
